import { datadogRum } from '@datadog/browser-rum';

import { AppConfig } from '@/utils/AppConfig';
import { isDatadogInitialized } from '@/utils/Datadog';

// Flow Types
export enum BookingFlow {
  DIRECT_TO_INITIAL_NON_INSURANCE = 'DIRECT_TO_INITIAL_NON_INSURANCE',
  DIRECT_TO_INITIAL_INSURANCE = 'DIRECT_TO_INITIAL_INSURANCE',
  ASK_A_DIETITIAN = 'ASK_A_DIETITIAN',
  DISCOVERY_CALL = 'DISCOVERY_CALL',
  GLP_INSURANCE = 'GLP_INSURANCE',
  GLP_NON_INSURANCE = 'GLP_NON_INSURANCE',
}

export enum DtiScreensNonInsurance {
  LANDING = 'book_your_first_session',
  LEAD_FORM = 'patient_information',
  PROFILE_ALREADY_EXISTS = 'profile_already_exists',
  INTERSTITIAL_FIND_RD = 'interstitial_find_rd',
  REASON_FOR_VISIT = 'reason_for_visit',
  CHOOSE_RD = 'choose_rd',
  MEMBERSHIP = 'choose_self_pay_plan',
  PAYMENT = 'payment_details',
  SUCCESS = 'confirmation_self_pay',
}

export enum DtiScreensInsurance {
  LANDING = 'book_your_first_session',
  LEAD_FORM = 'patient_information',
  PROFILE_ALREADY_EXISTS = 'profile_already_exists',
  NETWORK_STATUS_IN_NETWORK = 'network_status_in_network',
  NETWORK_STATUS_OUT_OF_NETWORK = 'network_status_out_of_network',
  INTERSTITIAL_FIND_RD = 'interstitial_find_rd',
  REASON_FOR_VISIT = 'reason_for_visit',
  CHOOSE_RD = 'choose_rd',
  INSURANCE_CARD_UPLOAD = 'insurance_card_upload',
  INSURANCE_PATIENT_INFORMATION = 'insurance_patient_info',
  PAYMENT = 'upload_cc-insurance',
  SUCCESS = 'confirmation_insurance',
}

export enum GlpScreensNonInsurance {
  BASIC_FORM = 'patient_basic_information',
  PROFILE_ALREADY_EXISTS = 'profile_already_exists',
  OFFERING = 'offering_screen',
  DETAILS_FORM = 'patient_details_information',
  CHOOSE_RD = 'choose_rd',
  PAYMENT = 'payment_details',
  SUCCESS = 'confirmation_comp',
}

export enum GlpScreensInsurance {
  BASIC_FORM = 'patient_basic_information',
  PROFILE_ALREADY_EXISTS = 'profile_already_exists',
  OFFERING = 'offering_screen',
  DETAILS_FORM = 'patient_details_information',
  CHOOSE_RD = 'choose_rd',
  PAYMENT = 'payment_details',
  SUCCESS = 'confirmation_comp',
}

// Screen name mapping for better readability in Datadog
export const SCREEN_NAMES_NON_INSURANCE = {
  [DtiScreensNonInsurance.LANDING]: 'Book your first session', // STEP 0
  [DtiScreensNonInsurance.LEAD_FORM]: 'Patient Information', // STEP 1
  [DtiScreensNonInsurance.PROFILE_ALREADY_EXISTS]: 'Profile Already Exists', // STEP 9
  [DtiScreensNonInsurance.INTERSTITIAL_FIND_RD]: 'Interstitial Find-RD', // STEP 2
  [DtiScreensNonInsurance.REASON_FOR_VISIT]: 'Reason For Visit', // STEP 2
  [DtiScreensNonInsurance.CHOOSE_RD]: 'Choose RD', // STEP 3
  [DtiScreensNonInsurance.MEMBERSHIP]: 'Choose Self Pay Plan', // STEP 4
  [DtiScreensNonInsurance.PAYMENT]: 'Payment Details', // STEP 5
  [DtiScreensNonInsurance.SUCCESS]: 'Confirmation Self Pay', // STEP 6
};

export const SCREEN_NAMES_INSURANCE = {
  [DtiScreensInsurance.LANDING]: 'Book your first session', // STEP 0
  [DtiScreensInsurance.LEAD_FORM]: 'Patient Information', // STEP 1
  [DtiScreensInsurance.PROFILE_ALREADY_EXISTS]: 'Profile Already Exists', // STEP 9
  [DtiScreensInsurance.NETWORK_STATUS_IN_NETWORK]:
    'Network Status - In Network', // STEP 2
  [DtiScreensInsurance.NETWORK_STATUS_OUT_OF_NETWORK]:
    'Network Status - Out of Network', // STEP 3
  [DtiScreensInsurance.INTERSTITIAL_FIND_RD]: 'Interstitial Find-RD', // STEP 4
  [DtiScreensInsurance.REASON_FOR_VISIT]: 'Reason For Visit', // STEP 4
  [DtiScreensInsurance.CHOOSE_RD]: 'Choose RD', // STEP 5
  [DtiScreensInsurance.INSURANCE_CARD_UPLOAD]: 'Insurance Card Upload', // STEP 6
  [DtiScreensInsurance.INSURANCE_PATIENT_INFORMATION]:
    'Insurance Patient Information', // STEP 6
  [DtiScreensInsurance.PAYMENT]: 'Upload CC-Insurance', // STEP 7
  [DtiScreensInsurance.SUCCESS]: 'Confirmation Insurance', // STEP 8
};

export const SCREEN_NAMES_GLP_NON_INSURANCE = {
  [GlpScreensNonInsurance.BASIC_FORM]: 'Patient Basic Information', // STEP 0
  [GlpScreensNonInsurance.PROFILE_ALREADY_EXISTS]: 'Profile Already Exists', // STEP 9
  [GlpScreensNonInsurance.OFFERING]: 'Offering Screen', // STEP 1
  [GlpScreensNonInsurance.DETAILS_FORM]: 'Patient Details Information', // STEP 2
  [GlpScreensNonInsurance.CHOOSE_RD]: 'Choose RD', // STEP 3
  [GlpScreensNonInsurance.SUCCESS]: 'Confirmation Self Pay', // STEP 4
  [GlpScreensNonInsurance.PAYMENT]: 'Payment Details', // STEP 5
};

export const SCREEN_NAMES_GLP_INSURANCE = {
  [GlpScreensNonInsurance.BASIC_FORM]: 'Patient Basic Information', // STEP 0
  [GlpScreensNonInsurance.PROFILE_ALREADY_EXISTS]: 'Profile Already Exists', // STEP 9
  [GlpScreensNonInsurance.OFFERING]: 'Offering Screen', // STEP 1
  [GlpScreensNonInsurance.DETAILS_FORM]: 'Patient Details Information', // STEP 2
  [GlpScreensNonInsurance.CHOOSE_RD]: 'Choose RD', // STEP 3
  [GlpScreensNonInsurance.SUCCESS]: 'Confirmation Self Pay', // STEP 4
  [GlpScreensNonInsurance.PAYMENT]: 'Payment Details', // STEP 5
};

// Type definition that includes all possible screen enums
export type DTIScreenTypes = DtiScreensInsurance | DtiScreensNonInsurance;
export type GLPScreenTypes = GlpScreensInsurance | GlpScreensNonInsurance;

interface ScreenViewData {
  email?: string;
  reasonForVisit?: string;
  insuranceProvider?: string;
  state?: string;
  userId?: string;
  step?: string;
  membershipType?: string;
  selectedProvider?: string;
  selectedSlot?: string;
  insuranceFlow?: boolean;
  previousScreen?: string;
  transitionTo?: string;
  [key: string]: any;
}

/**
 * Get appropriate screen name mapping based on flow type
 */
const getScreenName = (
  flow: BookingFlow,
  screen: DTIScreenTypes | GLPScreenTypes,
): string => {
  // For new flow-specific screens
  if (flow === BookingFlow.DIRECT_TO_INITIAL_INSURANCE) {
    return (
      SCREEN_NAMES_INSURANCE[screen as DtiScreensInsurance] || String(screen)
    );
  }
  if (flow === BookingFlow.DIRECT_TO_INITIAL_NON_INSURANCE) {
    return (
      SCREEN_NAMES_NON_INSURANCE[screen as DtiScreensNonInsurance] ||
      String(screen)
    );
  }
  if (flow === BookingFlow.GLP_INSURANCE) {
    return (
      SCREEN_NAMES_GLP_INSURANCE[screen as GlpScreensInsurance] ||
      String(screen)
    );
  }

  if (flow === BookingFlow.GLP_NON_INSURANCE) {
    return (
      SCREEN_NAMES_GLP_NON_INSURANCE[screen as GlpScreensNonInsurance] ||
      String(screen)
    );
  }

  // For RD matching steps or other flows
  return String(screen);
};

/**
 * Track screen view in the booking funnel using RUM events
 */
export const trackScreenView = (
  flow: BookingFlow,
  screen: DTIScreenTypes | GLPScreenTypes,
  data?: ScreenViewData,
) => {
  // Only track if Datadog is initialized
  if (isDatadogInitialized()) {
    const screenName = getScreenName(flow, screen);

    datadogRum.addAction(screenName, {
      type: 'screen_view',
      flow,
      screen,
      screen_name: screenName,
      timestamp: new Date().toISOString(),
      ...data,
      service: 'culina-start',
      path: window.location.pathname,
      env: AppConfig.env,
    });
  }
};

/**
 * Specialized function to track RD matching process screens
 * with appropriate flow type (insurance or non-insurance)
 */
export const trackRDMatchingScreen = (
  step: any,

  isInsuranceFlow: boolean,
  data?: ScreenViewData,
) => {
  const flow = isInsuranceFlow
    ? BookingFlow.DIRECT_TO_INITIAL_INSURANCE
    : BookingFlow.DIRECT_TO_INITIAL_NON_INSURANCE;

  trackScreenView(flow, step, {
    ...data,
    insuranceFlow: isInsuranceFlow,
  });
};
